import * as Yup from 'yup'
import { Formik, FormikHelpers } from 'formik'
import { useMutation } from '@tanstack/react-query'
import { useToast } from '@machobear/design-system'
import { useState } from 'react'
import { isEmpty, omit } from 'lodash'
import moment from 'moment'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router'
import { Userpilot } from 'userpilot'
import { useTranslation } from 'react-i18next'
import { boostSponsoredListing } from '@/models/plans/apis'
import useProfile from '@/hooks/use-profile'
import { useSponsorListing } from '@/hooks/use-sponsored-listing'
import { useSponsoredListingCreditInformation } from '@/hooks/use-block-and-pay'
import useCurrentSession from '@/hooks/use-current-session'
import { show as showVerifyPhoneModal } from '@/components/verify-phone-modal'
import { pushToDatalayer } from '@/utils/push-datalayer'
import { validNumber, validExpirationDate, validCvv } from '@/utils/payments'
import { SummaryStateDriver } from './summary-state-driver'
import { FormBoxSkeleton } from '../components'
import { useBoostingPackages } from '../hooks/use-boosting-packages'
import { Listing } from '../types'

export const PackageNameToDurationMap = {
  'seven-days-sponsored-listing-package': 7,
  'fourteen-days-sponsored-listing-package': 14,
  'thirty-days-sponsored-listing-package': 28
}

const PackageNameToUserPilotIdMap = {
  'seven-days-sponsored-listing-package': 'goal_purchase_sl_7d',
  'fourteen-days-sponsored-listing-package': 'goal_purchase_sl_14d',
  'thirty-days-sponsored-listing-package': 'goal_purchase_sl_28d'
}

const ListingSummaryForm = ({ data, isLoading }: { data: Listing; isLoading: boolean }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const session = useCurrentSession()
  const profile = useProfile()
  const toast = useToast()
  const [doValidate, setDoValidate] = useState(false)
  const enableValidation = () => setDoValidate(true)
  const { packages } = useBoostingPackages()
  const usage = useSponsoredListingCreditInformation()

  const boostSponsoredListingMutation = useMutation({
    mutationFn: (payload: any) => {
      const { body, listingId } = payload
      return boostSponsoredListing({ body }, { listingId })
    }
  })

  const { purchaseSponsoredListing } = useSponsorListing(data.listing_id)

  const initialValues = {
    first_name: '',
    last_name: '',
    card_number: '',
    expiration_date: '',
    security_code: '',
    street1: '',
    street2: '',
    state: '',
    city: '',
    country: '',
    zip: '',
    save_payment_method: 'true'
  }

  const [validCVCLength, setValidCVCLength] = useState(null)
  const acceptedCards = ['visa', 'mastercard', 'american-express', 'unionpay']

  const isCardNumberValid = (cardNumber: string) => {
    const { card, isValid }: any = validNumber(cardNumber)
    setValidCVCLength(card ? card?.code.size : null)
    return acceptedCards.includes(card?.type) && isValid
  }

  const isExpirationValid = (expiration: string) => {
    const { isValid: isExpValid } = validExpirationDate(expiration)
    return isExpValid
  }

  const isSecurityCodeValid = (securityCode: string) => {
    if (validCVCLength) {
      const { isValid: isCodeValid } = validCvv(securityCode, validCVCLength)
      return isCodeValid
    }
    return true
  }

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().when(['duration', 'hasEnoughCredits'], {
      is: (duration: string, hasEnoughCredits: boolean) => !!duration && !hasEnoughCredits,
      then: Yup.string().required('Required'),
      otherwise: Yup.string()
    }),
    last_name: Yup.string().when(['duration', 'hasEnoughCredits'], {
      is: (duration: string, hasEnoughCredits: boolean) => !!duration && !hasEnoughCredits,
      then: Yup.string().required('Required'),
      otherwise: Yup.string()
    }),
    card_number: Yup.string()
      .transform(value => value.replace(/\s/g, ''))
      .when(['duration', 'hasEnoughCredits'], {
        is: (duration: string, hasEnoughCredits: boolean) => !!duration && !hasEnoughCredits,
        then: Yup.string()
          .required()
          .test(
            'valid card',
            t('r.payment.edit.invalid_card_number.error', 'Invalid card number'),
            value => isCardNumberValid(value ? value : '')
          ),
        otherwise: Yup.string()
      }),
    expiration_date: Yup.string()
      .transform(value => value.replace('/', ''))
      .when(['duration', 'hasEnoughCredits'], {
        is: (duration: string, hasEnoughCredits: boolean) => !!duration && !hasEnoughCredits,
        then: Yup.string()
          .required()
          .test('valid expiration', t('b.checkout.error.expiry', 'Invalid date'), value =>
            isExpirationValid(value ? value : '')
          ),
        otherwise: Yup.string()
      }),
    security_code: Yup.string().when(['duration', 'hasEnoughCredits'], {
      is: (duration: string, hasEnoughCredits: boolean) => !!duration && !hasEnoughCredits,
      then: Yup.string()
        .required()
        .min(3, t('b.checkout.error.cvc', 'Invalid CVC'))
        .test('cvc', t('b.checkout.error.cvc', 'Invalid CVC'), value =>
          isSecurityCodeValid(value ? value : '')
        ),
      otherwise: Yup.string()
    }),
    street1: Yup.string().when(['duration', 'hasEnoughCredits'], {
      is: (duration: string, hasEnoughCredits: boolean) => !!duration && !hasEnoughCredits,
      then: Yup.string().required('Required'),
      otherwise: Yup.string()
    }),
    city: Yup.string().when(['duration', 'hasEnoughCredits'], {
      is: (duration: string, hasEnoughCredits: boolean) => !!duration && !hasEnoughCredits,
      then: Yup.string().required('Required'),
      otherwise: Yup.string()
    }),
    state: Yup.string().when(['duration', 'hasEnoughCredits'], {
      is: (duration: string, hasEnoughCredits: boolean) => !!duration && !hasEnoughCredits,
      then: Yup.string().required('Required'),
      otherwise: Yup.string()
    }),
    zip: Yup.string().when(['duration', 'hasEnoughCredits'], {
      is: (duration: string, hasEnoughCredits: boolean) => !!duration && !hasEnoughCredits,
      then: Yup.string().required('Required'),
      otherwise: Yup.string()
    }),
    street2: Yup.string(),
    country: Yup.string(),
    discount_code: Yup.string(),
    save_payment_method: Yup.string()
  })

  const onSubmit = async (values: any, { setSubmitting }: FormikHelpers<any>) => {
    if (isEmpty(session.phone_number) || session.phone_verified_state !== 'passed') {
      showVerifyPhoneModal({ history })
      setSubmitting(false)
      return
    }

    // listing boost requested
    if (values.duration) {
      let resp, payment_method_id

      payment_method_id = values.payment_method_id

      // create payment method if none
      if (!payment_method_id) {
        const payload = {
          ...omit(values, ['duration', 'discount_code', 'hasEnoughCredits']),
          expiration_date: values.expiration_date.replace('/', ''),
          card_number: values.card_number.replace(/ /g, ''),
          payment_method: 'stripe',
          default_payment_method: 'true'
        }

        try {
          resp = await dispatch.plans.addPaymentMethod(payload)
          if (resp?.response?.ok === false) {
            throw new Error()
          }
          payment_method_id = resp?.body?.[0]?.payment_method_id
        } catch (error) {
          toast({
            message: 'Unable to add payment method',
            tone: 'critical'
          })
          return
        }
      }

      if (values.update_payment) {
        const payload = {
          ...omit(values, [
            'duration',
            'discount_code',
            'payment_method_id',
            'update_payment',
            'hasEnoughCredits'
          ]),
          expiration_date: values.expiration_date.replace('/', ''),
          card_number: values.card_number.replace(/ /g, ''),
          payment_method: 'stripe',
          default_payment_method: 'true'
        }

        try {
          resp = await dispatch.plans.updatePaymentMethod(payload, payment_method_id)
          if (resp?.response?.ok === false) {
            throw new Error()
          }
          payment_method_id = resp?.body?.[0]?.payment_method_id
        } catch (error) {
          toast({
            message: 'Unable to update the payment detail',
            tone: 'critical'
          })
          return
        }
      }

      // insufficient credits for selected boosting package
      if (
        usage.availableCredits <
        PackageNameToDurationMap[values.duration as keyof typeof PackageNameToDurationMap]
      ) {
        try {
          resp = await purchaseSponsoredListing.mutateAsync({
            planName: values.duration,
            paymentMethodId: payment_method_id,
            discountCode: values.discount_code
          })
          if (!resp?.response?.ok) {
            throw new Error(resp?.body?.message)
          }
        } catch (error) {
          toast({
            message: 'Unable to purchase credits for the boost of listing',
            tone: 'critical'
          })
          return
        }

        const price =
          packages.find((pkg: any) => pkg.plan_name === values.duration)?.total_before_tax ?? '-1' // -1 to signify problem
        Userpilot.track(
          PackageNameToUserPilotIdMap[values.duration as keyof typeof PackageNameToDurationMap],
          { price }
        )
      }

      // consume the credits for the boost
      try {
        resp = await boostSponsoredListingMutation.mutateAsync({
          body: {
            planned_start_date: moment(new Date()).format('YYYY-MM-DD'),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            plan_name: values.duration,
            product_name: 'sponsored-listing-product',
            immediate_start: true
          },
          listingId: data.listing_id
        })
        if (!resp?.response?.ok) {
          throw new Error(resp?.body?.message)
        }
      } catch (error) {
        console.log(error)
        toast({
          message: 'Unable to apply the boost to listing',
          tone: 'critical'
        })
        return
      }
      pushToDatalayer({
        event: 'create-application-sponsor-listing-credit-checkout-submit-purchase-success',
        purchaseId: values.duration
      })
      pushToDatalayer({
        event: 'app_sponsor_cr_purchased'
      })

      // stop client from sending promotion events
      localStorage.removeItem('promoType')
    }

    pushToDatalayer(
      values.discount_code
        ? {
            event: 'application-publish-listing-submit',
            couponCode: values.discount_code
          }
        : {
            event: 'application-publish-listing-submit'
          }
    )

    try {
      const { response } = await dispatch.listing.update(
        {
          listing_id: data.listing_id,
          state_machine: 'published'
        },
        { noLoading: true }
      )

      if (!response?.ok) {
        throw new Error(response?.body?.message)
      }
    } catch (error) {
      console.log(error)
      toast({
        message: 'Unable to publish the listing',
        tone: 'critical'
      })
      return
    }

    toast({
      message: t('l.listing.edit.listing_edited_toast_text', 'Your listing has been saved!'),
      tone: 'positive'
    })

    // cleanup
    localStorage.removeItem('selectedBoostingPackage')
    history.push(`/listing/${data.listing_id}`)
  }

  if (isLoading) {
    return <FormBoxSkeleton />
  }

  return (
    <>
      <Formik
        onSubmit={onSubmit}
        initialValues={{
          ...initialValues,
          first_name: profile?.first_name,
          last_name: profile?.last_name,
          country: 'CA',
          discount_code: ''
        }}
        validationSchema={doValidate ? validationSchema : Yup.object()}
        validateOnMount={true}
      >
        <SummaryStateDriver data={data} enableValidation={enableValidation} />
      </Formik>
    </>
  )
}

export default ListingSummaryForm
